// API Routes helper - Matches WordPress API documentation
// All endpoints use /sdg/v1/ as documented in API-Documentation.md
//
// WordPress backend origin (set via deploy target — see config/TARGETS.md):
// - npm start            → local.env
// - npm run build:iis    → askdash.rks-gov.net
// - npm run build:ubuntu → dashboard.sdg.unkt.org
// REACT_APP_API_ORIGIN is baked in at build time from .env.production / .env.development
export const ORIGIN =
  process.env.REACT_APP_API_ORIGIN ||
  (process.env.NODE_ENV !== 'production'
    ? 'http://localhost:8080/unkt-dashboard'
    : 'https://dashboard.sdg.unkt.org');
export const API_BASE = `${ORIGIN}/wp-json/wp/v2`;
export const ACF_API_BASE = `${ORIGIN}/wp-json/acf/v2`;
export const SDG_API_BASE = `${ORIGIN}/wp-json/sdg/v1`;
export const DEPLOY_TARGET = process.env.REACT_APP_DEPLOY_TARGET || 'unknown';

// Dev-time visibility of the active API origin
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('[API] ORIGIN =', ORIGIN, '| target =', DEPLOY_TARGET);
}

export type GoalType = 'un' | 'eu';

/**
 * API route helpers according to API Documentation
 * - List endpoints return minimal data (id, title)
 * - Preview endpoints return comprehensive data (relationships, assessment, graph, etc.)
 * - Use preview endpoints when you need full entity data
 */
export const route = {
  // General data endpoint
  generalData: () => `${ACF_API_BASE}/options/website-general-data`,
  
  // ===== LIST ENDPOINTS (minimal data: id, title) =====
  
  // Goals list - returns all goals (id, title)
  goalsList: () => `${SDG_API_BASE}/goals`,
  
  // Targets list - returns targets for a type, optionally filtered by goal_id
  targetsList: (type: GoalType, goalId?: number) => {
    const typeMap = type === 'eu' ? 'eu' : 'un';
    const goalParam = goalId ? `?goal_id=${goalId}` : '';
    return `${SDG_API_BASE}/targets/${typeMap}${goalParam}`;
  },
  
  // Indicators list - returns indicators for a type, optionally filtered by target_id (excludes incomplete by default)
  indicatorsList: (type: GoalType, targetId?: number) => {
    const typeMap = type === 'eu' ? 'eu' : 'un';
    const targetParam = targetId ? `?target_id=${targetId}` : '';
    return `${SDG_API_BASE}/indicators/${typeMap}${targetParam}`;
  },
  // Indicators list including incomplete (for catalog page); each item has setup_complete, goal_id, target_id
  indicatorsListWithIncomplete: (type: GoalType) => {
    const typeMap = type === 'eu' ? 'eu' : 'un';
    return `${SDG_API_BASE}/indicators/${typeMap}?include_incomplete=1`;
  },
  
  // ===== PREVIEW ENDPOINTS (comprehensive data) =====
  
  // Goal preview - returns full goal data with linked_targets, linked_indicators, assessment, etc.
  goalPreview: (goalId: number, useCache: boolean = false, goalType?: GoalType) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = goalType ? `&type=${goalType}` : '';
    return `${SDG_API_BASE}/goal/${goalId}/preview${cacheParam}${typeParam}`;
  },
  
  // Target preview - returns full target data with goal, linked_indicators, assessment, etc.
  targetPreview: (targetId: number, useCache: boolean = false) => {
    if (useCache) {
      return `${SDG_API_BASE}/target/${targetId}/preview?cache=1`;
    }
    return `${SDG_API_BASE}/target/${targetId}/preview?cache=0&_t=${Date.now()}`;
  },
  
  // Indicator preview - returns full indicator data with target, assessment, graph, datasets, etc.
  indicatorPreview: (indicatorId: number, useCache: boolean = false) => {
    if (useCache) {
      return `${SDG_API_BASE}/indicator/${indicatorId}/preview?cache=1`;
    } else {
      // Add cache=0 and timestamp to prevent browser caching
      const timestamp = Date.now();
      return `${SDG_API_BASE}/indicator/${indicatorId}/preview?cache=0&_t=${timestamp}`;
    }
  },
  
  // ===== ASSESSMENT ENDPOINTS (assessment data only) =====
  
  goalAssessment: (goalId: number, useCache: boolean = false, goalType?: GoalType) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = goalType ? `&type=${goalType}` : '';
    return `${SDG_API_BASE}/goal/${goalId}/assessment${cacheParam}${typeParam}`;
  },
  
  targetAssessment: (targetId: number, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '';
    return `${SDG_API_BASE}/target/${targetId}/assessment${cacheParam}`;
  },
  
  indicatorAssessment: (indicatorId: number, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '';
    return `${SDG_API_BASE}/indicator/${indicatorId}/assessment${cacheParam}`;
  },
  
  // ===== BATCH PREVIEW ENDPOINTS =====
  
  // Batch Goals Preview - returns all goals with full data
  goalsPreviewBatch: (goalType?: GoalType, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = goalType ? `&type=${goalType}` : '';
    return `${SDG_API_BASE}/goals/preview${cacheParam}${typeParam}`;
  },
  
  // Batch Targets Preview - returns targets with full data
  targetsPreviewBatch: (goalType?: GoalType, goalId?: number, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = goalType ? `&type=${goalType}` : '';
    const goalParam = goalId ? `&goal_id=${goalId}` : '';
    return `${SDG_API_BASE}/targets/preview${cacheParam}${typeParam}${goalParam}`;
  },
  
  // Batch Indicators Preview - returns indicators with full data
  indicatorsPreviewBatch: (goalType: GoalType, goalId?: number, targetId?: number, limit?: number, offset?: number, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = `&type=${goalType}`;
    const goalParam = goalId ? `&goal_id=${goalId}` : '';
    const targetParam = targetId ? `&target_id=${targetId}` : '';
    const limitParam = limit ? `&limit=${limit}` : '';
    const offsetParam = offset ? `&offset=${offset}` : '';
    return `${SDG_API_BASE}/indicators/preview${cacheParam}${typeParam}${goalParam}${targetParam}${limitParam}${offsetParam}`;
  },
  
  // ===== ASSESSMENT SUMMARY ENDPOINTS =====
  
  // Goals Assessment Summary - returns all goals with assessments and summary statistics
  goalsAssessmentSummary: (goalType?: GoalType, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = goalType ? `&type=${goalType}` : '';
    return `${SDG_API_BASE}/goals/assessment-summary${cacheParam}${typeParam}`;
  },
  
  // ===== PROFILE DATA ENDPOINT =====
  
  // Profile Data - all-in-one endpoint for SDG Profile Page
  profileData: (goalType: GoalType, useCache: boolean = false) => {
    const cacheParam = useCache ? '?cache=1' : '?cache=0';
    const typeParam = `&type=${goalType}`;
    return `${SDG_API_BASE}/profile-data${cacheParam}${typeParam}`;
  },
};
